import { IAttachment } from '@alberta/konexi-shared';

import { AttachmentDto } from '../../models/attachment/attachment-dto.model';
import { UploadData } from './attachment-transfer.service';

export function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (blob && blob instanceof Blob && typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer();
  }

  if (blob == null || !(blob instanceof Blob)) {
    return null;
  }

  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onloadend = () => {
      resolve(fileReader.result as ArrayBuffer);
    };
    fileReader.onerror = () => {
      reject();
    };
    fileReader.readAsArrayBuffer(blob);
  });
}

export function arrayBufferToBlob(buffer: ArrayBuffer, mimeType: string): Blob {
  return new Blob([buffer], { type: mimeType });
}

export function base64ToBlob(b64Data, contentType: string = '', sliceSize: number = 512): Blob {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = [slice.length];
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  return new Blob(byteArrays, { type: contentType });
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => {
      window.logger.info('Failed to read blob.');
      reject(reader.error);
    };
    reader.readAsDataURL(blob);
  });
}

export function getMimeTypeFromBase64String(base64String: string): string {
  if (typeof base64String !== 'string') {
    return;
  }
  const mime = base64String.match(/data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/);
  return mime && mime.length ? mime[1] : null;
}

export function stripDataPartFromBase64String(base64String: string): string {
  return base64String.substring(base64String.indexOf(',') + 1);
}

const EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  'image/jpeg': 'jpeg',
  'image/jpg': 'jpeg',
  'image/png': 'png',
  'application/pdf': 'pdf',
};

/**
 * The file extension a base64 data URL should be stored under.
 *
 * `prepareAttachmentData` takes the stored filename's extension from the
 * metadata `name`, while the content type comes from the data itself — so a
 * caller that hardcodes the extension ends up with a PDF named `.jpeg`.
 *
 * Unknown types fall back to jpeg, which is what those call sites assumed back
 * when an image was the only thing they could produce.
 */
export function getFileExtensionFromBase64String(base64String: string): string {
  const mimeType = getMimeTypeFromBase64String(base64String);
  return EXTENSION_BY_MIME_TYPE[mimeType] ?? 'jpeg';
}

/**
 * Resolves the id under which other entities reference an image: the
 * `metadata.uniqueId` (fallback `_id`) of the NEWEST of the given images.
 * Picking among several images is logged under the given `logContext`.
 *
 * The UI allows one image per node, but storage can still hold several, e.g.:
 * - a replaced image whose deferred deletion has not run (or failed)
 * - two offline devices attached a photo to the same node and both synced
 *
 * A local image that was not uploaded yet carries no date and wins against
 * anything persisted — so a fresh photo always beats a stale leftover.
 */
export function getNewestImageUniqueId(images: Partial<IAttachment>[], logContext: string): string | undefined {
  if (!images.length) {
    return undefined;
  }
  let image = images[0];
  if (images.length > 1) {
    window.logger.info(`${logContext} has ${images.length} images, referencing the newest`);
    image = images.reduce((newest, candidate) =>
      getImageTimestamp(candidate) >= getImageTimestamp(newest) ? candidate : newest
    );
  }
  return image.metadata?.uniqueId ?? image._id;
}

function getImageTimestamp(image: Partial<IAttachment>): number {
  const date = image.uploadDate ?? image.metadata?.createdAt;
  // local images that were not uploaded yet carry no date — they are always
  // newer than anything persisted
  return date ? new Date(date).getTime() : Number.MAX_SAFE_INTEGER;
}

/**
 * Sending a Blob over a websocket through socket-io will automatically transform them to ArrayBuffers
 * loosing the Blob.type information.
 * This method will convert it directly, saving the information in the mime property.
 */
export async function prepareDataForUpload(
  data: UploadData
): Promise<Partial<AttachmentDto> & { mime: string; blob: ArrayBuffer }> {
  const dataToSend = { ...data };

  if (dataToSend.blob instanceof Blob) {
    dataToSend.mime = dataToSend.blob.type;
    dataToSend.blob = await blobToArrayBuffer(dataToSend.blob);
  }

  return dataToSend as any;
}
